import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Badge,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { FaArrowLeft, FaEllipsisV, FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { meetingsApi } from '../../../../services/api/meetingsApi';
import { useAlert } from '../../../../contexts/AlertContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { BackButton } from '../../../common/styles';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EmptyState from '../../../common/EmptyState';
import ConfirmationModal from '../../../common/ConfirmationModal';
import { COLORS } from '../../../../constants/radar';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  StatusBadge,
  MainContainer,
} from '../../styles';
import GeneralTab from './GeneralTab';
import ConfigureTalkingPointsTab from './ConfigureTalkingPointsTab';
import DiscussionTab from './DiscussionTab';
import PreviousMeetingsTab from './PreviousMeetingsTab';
import HistoryTab from './HistoryTab';
import { MEETING_ATTENDEE_TYPES } from '../../../../constants/meetingAttendeeTypes';
import { isMeetingEnded } from '../../../../constants/meetingStatus';

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.BACKGROUND_SECONDARY};
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0 16px;
  margin-bottom: 0;
  background-color: ${COLORS.BACKGROUND_PRIMARY};
`;

const TabButton = styled.button`
  padding: 12px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${({ 'data-active': active }) => active === 'true' ? COLORS.PRIMARY : COLORS.GRAY};
  border-bottom: 2px solid ${({ 'data-active': active }) => active === 'true' ? COLORS.PRIMARY : 'transparent'};
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: ${COLORS.PRIMARY};
  }
`;

const FormContainer = styled.div`
  flex: 1;
  background: white;
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'configure-talking-points', label: 'Talking Points' },
  { id: 'discussions', label: 'Discussions' },
  { id: 'previous-meetings', label: 'Previous Meetings' },
  { id: 'history', label: 'History' },
];

const MeetingForm = ({ meetingId, isViewMode = false, onBack }) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [meeting, setMeeting] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState(null);

  // Lookups
  const [attendeeTypes, setAttendeeTypes] = useState([]);
  const [meetingStatuses, setMeetingStatuses] = useState([]);

  // Permission state
  const [permissions, setPermissions] = useState({
    canEditGeneralInfo: false,
    canUpdateStatus: false,
    canAddTalkingPoints: false,
    canAddNotes: false,
    canAddActionItems: false,
    isReadOnly: true,
  });

  // Debounce timer ref
  const saveTimerRef = useRef(null);

  useEffect(() => {
    fetchMeetingData();
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [meetingId]);

  const fetchMeetingData = async () => {
    try {
      setIsLoading(true);
      const [meetingData, atTypes, statuses] = await Promise.all([
        meetingsApi.getMeetingById(meetingId),
        meetingsApi.getAttendeeTypes(),
        meetingsApi.getMeetingStatuses(),
      ]);

      setMeeting(meetingData);
      setAttendeeTypes(atTypes || []);
      setMeetingStatuses(statuses || []);

      // Calculate permissions
      const currentEmployeeId = user?.employee?.id;
      const isOwner = meetingData?.attendees?.some(
        a => a.employee?.id === currentEmployeeId && a.attendeeType?.code === MEETING_ATTENDEE_TYPES.OWNER
      );
      const isAttendee = meetingData?.attendees?.some(
        a => a.employee?.id === currentEmployeeId
      );
      const statusCode = meetingData?.currentStatus?.code;
      const hasEnded = isMeetingEnded(statusCode);

      setPermissions({
        canEditGeneralInfo: isOwner && !isViewMode,
        canUpdateStatus: isOwner && !isViewMode,
        canAddTalkingPoints: isAttendee && !isViewMode && (!hasEnded || isOwner),
        canAddNotes: isAttendee && !isViewMode && (!hasEnded || isOwner),
        canAddActionItems: isAttendee && !isViewMode && (!hasEnded || isOwner),
        isReadOnly: !isAttendee || isViewMode || (hasEnded && !isOwner),
      });
    } catch (err) {
      if (err.status === 403) {
        setError({ type: 'permission', message: 'You do not have permission to access this meeting' });
      } else if (err.status === 404) {
        setError({ type: 'notFound', message: 'Meeting not found' });
      } else {
        setError({ type: 'generic', message: 'Failed to load meeting' });
        showAlert('Failed to load meeting', 'error', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced save for general info
  const debouncedSave = useCallback((field, value) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(async () => {
      try {
        setIsSaving(true);
        const updateData = { [field]: value };
        await meetingsApi.updateMeeting(meetingId, updateData);
        setHasChanges(true);
      } catch (err) {
        showAlert(`Failed to save ${field}`, 'error', err);
        // Revert on error by refetching
        fetchMeetingData();
      } finally {
        setIsSaving(false);
      }
    }, 300);
  }, [meetingId, showAlert]);

  const handleFieldChange = (field, value) => {
    setMeeting(prev => ({ ...prev, [field]: value }));
    debouncedSave(field, value);
  };

  const handleStatusChange = async (statusId) => {
    try {
      setIsSaving(true);
      await meetingsApi.updateMeetingStatus(meetingId, statusId);
      const newStatus = meetingStatuses.find(s => s.id === statusId);
      setMeeting(prev => ({ ...prev, currentStatus: newStatus }));
      setHasChanges(true);
      showAlert('Status updated', 'success');
    } catch (err) {
      showAlert('Failed to update status', 'error', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await meetingsApi.deleteMeeting(meetingId);
      showAlert('Meeting deleted', 'success');
      onBack(true); // Always refresh list after delete
    } catch (err) {
      showAlert('Failed to delete meeting', 'error', err);
    }
    setDeleteDialogOpen(false);
  };

  // Called by child tabs when they make changes
  const handleChange = useCallback(() => {
    setHasChanges(true);
  }, []);

  const handleBack = useCallback(() => {
    onBack(hasChanges);
  }, [onBack, hasChanges]);

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const refreshMeeting = async () => {
    try {
      const meetingData = await meetingsApi.getMeetingById(meetingId);
      setMeeting(meetingData);
    } catch (err) {
      showAlert('Failed to refresh meeting data', 'error', err);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading meeting..." />;
  }

  if (!meeting) {
    return (
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={onBack}>
              <FaArrowLeft /> Back to Meetings
            </BackButton>
          </HeaderLeft>
        </Header>
        <MainContainer>
          <EmptyState
            title={error?.message || 'Meeting not found'}
            description={
              error?.type === 'permission'
                ? 'You are not an attendee of this meeting or do not have the required permissions to view it.'
                : error?.type === 'notFound'
                ? 'The meeting you are looking for does not exist or has been deleted.'
                : 'An error occurred while loading the meeting. Please try again later.'
            }
          />
        </MainContainer>
      </Container>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <GeneralTab
            meeting={meeting}
            attendeeTypes={attendeeTypes}
            permissions={permissions}
            onFieldChange={handleFieldChange}
            onStatusChange={handleStatusChange}
            onRefresh={refreshMeeting}
            onChange={handleChange}
          />
        );
      case 1:
        return (
          <ConfigureTalkingPointsTab
            meetingId={meetingId}
            meeting={meeting}
            permissions={permissions}
            onRefresh={refreshMeeting}
            onChange={handleChange}
          />
        );
      case 2:
        return (
          <DiscussionTab
            meetingId={meetingId}
            meeting={meeting}
            permissions={permissions}
            onRefresh={refreshMeeting}
            onChange={handleChange}
          />
        );
      case 3:
        return (
          <PreviousMeetingsTab
            meetingId={meetingId}
            meeting={meeting}
          />
        );
      case 4:
        return (
          <HistoryTab meetingId={meetingId} />
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Back to Meetings
          </BackButton>
          <Title>{meeting.subject || 'Meeting'}</Title>
          {isSaving && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, color: '#6B7280' }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2">Saving...</Typography>
            </Box>
          )}
        </HeaderLeft>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {(meeting.currentStatus || meeting.status) && (() => {
            const status = meeting.currentStatus || meeting.status;
            const color = status.color || '#6b7280';
            return (
              <StatusBadge
                $bgColor={`${color}15`}
                $color={color}
              >
                {status.name}
              </StatusBadge>
            );
          })()}
          {permissions.canEditGeneralInfo && (
            <>
              <IconButton onClick={handleMenuOpen}>
                <FaEllipsisV />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    setDeleteDialogOpen(true);
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <FaTrash style={{ marginRight: 8 }} /> Delete Meeting
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Header>

      <Content>
        <TabContainer>
          {TABS.map((tab, index) => {
            const getTalkingPointsCount = () => meeting.talkingPoints?.length || 0;

            let count = 0;
            if (tab.id === 'configure-talking-points') count = getTalkingPointsCount();

            return (
              <TabButton
                key={tab.id}
                data-active={(activeTab === index).toString()}
                onClick={() => setActiveTab(index)}
              >
                {tab.label}
                {count > 0 && (
                  <Badge
                    badgeContent={count}
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
              </TabButton>
            );
          })}
        </TabContainer>

        <FormContainer>
          {renderTabContent()}
        </FormContainer>
      </Content>

      <ConfirmationModal
        isOpen={deleteDialogOpen}
        title="Delete Meeting"
        message={`Are you sure you want to delete "${meeting.subject}"? This action cannot be undone.`}
        actionButtonText="Delete"
        actionButtonColor="#dc2626"
        onConfirm={handleDelete}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </Container>
  );
};

export default MeetingForm;
